import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, InputGroup, NumericInput, TextArea } from '@blueprintjs/core';
import { toast } from 'sonner';
import {
  editAssignment, fetchAssignments, fetchLeadSources, fetchReps, newIdempotencyKey, previewAssignment, saveAssignment,
  type AssignmentControls, type AssignmentPreview, type AssignmentRequest, type AssignmentSelection, type AssignmentShare, type BatchSummary, type ExclusionKind,
  type DuplicateMode, type HistoryQualifier, type LeadFilter, type PriorRepMode, type TimeBasis,
} from './api';
import { leadImportKeys } from './keys';
import { queryPolicies } from '../../utils/queryPolicies';
import { ErrorNote, Pill, REASON_LABEL, Segmented, fmtInt } from './ui';
import ExcludedLeadsDialog from './ExcludedLeadsDialog';
import { PickList } from '../ui/PickList';

type Mode = AssignmentSelection['mode'];
/** One recipient row; rep is the select's value, count is used only with "Set counts". */
interface Recipient { key: number; rep: string; count: number }
let recipientKey = 0;
const newRecipient = (rep = ''): Recipient => ({ key: ++recipientKey, rep, count: 100 });

/**
 * Named repeat rules. Each maps to explicit controls that are saved on the
 * assignment, so the stored policy is exact. "Skip leads already given to a
 * rep" is the default because it is the rule the business uses most.
 */
type PresetId = 'assign_all' | 'skip_given' | 'skip_received' | 'skip_sources' | 'custom';
const PRESETS: { id: PresetId; title: string; desc: string; controls?: AssignmentControls }[] = [
  { id: 'skip_given', title: 'Skip leads a rep already had', desc: 'Leave out phones any rep ever had, from any import.',
    controls: { duplicate_mode: 'include_repeats', prior_rep_mode: 'any_rep', prior_rep_history: 'ever' } },
  { id: 'skip_received', title: 'Skip anything received before', desc: 'Leave out phones that came in an earlier import.',
    controls: { duplicate_mode: 'exclude_any', history_qualifier: 'received', time_basis: 'earlier_batch_date', prior_rep_mode: 'none' } },
  { id: 'skip_sources', title: 'Skip repeats from chosen sources', desc: 'Leave out phones that came earlier from the sources you check.',
    controls: { duplicate_mode: 'exclude_selected_sources', history_qualifier: 'received', time_basis: 'earlier_batch_date', prior_rep_mode: 'none' } },
  { id: 'assign_all', title: 'Include repeats', desc: 'Every assignable lead, repeats included.',
    controls: { duplicate_mode: 'include_repeats', prior_rep_mode: 'none' } },
  { id: 'custom', title: 'Custom rule', desc: 'Set the repeat and prior-rep filters yourself.' },
];

/** Earlier, later and other are always relative to the lead's own import. */
function describeRule(c: AssignmentControls, sourceNames: Map<number, string>): string {
  const parts: string[] = [];
  const when = c.time_basis === 'accepted_before' ? 'in an import accepted before its own' : c.time_basis === 'earlier_batch_date' ? 'in an import with an earlier batch date' : 'in any other import';
  const what = c.history_qualifier === 'assigned_current' ? 'is assigned now' : c.history_qualifier === 'assigned_ever' ? 'was ever assigned' : 'was received';
  if (c.duplicate_mode === 'exclude_any') parts.push(`skip a lead if another copy ${what} ${when}`);
  if (c.duplicate_mode === 'exclude_selected_sources') {
    const names = (c.source_ids ?? []).map((id) => sourceNames.get(id)).filter(Boolean).join(', ') || 'the checked sources';
    parts.push(`skip a lead if a copy from ${names} ${what} ${when}`);
  }
  if (c.prior_rep_mode === 'any_rep') parts.push(`skip a lead if any rep ${c.prior_rep_history === 'ever' ? 'ever had' : 'has'} another copy`);
  if (c.prior_rep_mode === 'selected_reps') parts.push(`skip a lead if a selected rep ${c.prior_rep_history === 'ever' ? 'ever had' : 'has'} another copy`);
  return parts.length ? parts.join('; ') + '.' : 'assign every assignable lead, repeats included.';
}

/**
 * Gives the chosen leads to one rep, or splits them between several. Each rep
 * still gets their own saved assignment (Oct 15 call: one assignment, one
 * rep); a split saves them together. Starts on the usual case: one rep, the
 * first 300 leads, skipping phones a rep already had. With a batchId the leads
 * come from that import; without one, from whatever imports viewFilter covers.
 */
const DEFAULT_PRESET = PRESETS[0];
export default function AssignPanel({ batchId = null, sourceId = null, summary, selectedIds, allMatching = false, viewFilter, sort, desc, matching, onSaved }: {
  batchId?: number | null;
  sourceId?: number | null;
  summary?: BatchSummary;
  selectedIds: number[];
  /** The grid chose every matching lead: start on "All matching". */
  allMatching?: boolean;
  viewFilter: LeadFilter;
  sort: string;
  desc: boolean;
  /** How many leads the view filter matches. */
  matching?: number;
  onSaved: () => void;
}) {
  const queryClient = useQueryClient();
  const across = batchId == null;
  const reps = useQuery({ queryKey: leadImportKeys.reps, queryFn: fetchReps, ...queryPolicies.configuration });
  const existing = useQuery({ queryKey: leadImportKeys.assignments(batchId), queryFn: () => fetchAssignments(batchId), ...queryPolicies.pagedData });
  const allSources = useQuery({ queryKey: leadImportKeys.sources, queryFn: fetchLeadSources, enabled: across, ...queryPolicies.configuration });

  const [recipients, setRecipients] = useState<Recipient[]>(() => [newRecipient()]);
  const [splitBy, setSplitBy] = useState<'even' | 'counts'>('even');
  const initialMode: Mode = allMatching ? 'all' : selectedIds.length > 0 ? 'manual' : 'first_n';
  const [mode, setMode] = useState<Mode | undefined>(initialMode);
  const [n, setN] = useState<number>(300);
  const [dup, setDup] = useState<DuplicateMode | undefined>(DEFAULT_PRESET.controls?.duplicate_mode);
  const [qualifier, setQualifier] = useState<HistoryQualifier | undefined>(DEFAULT_PRESET.controls?.history_qualifier);
  const [basis, setBasis] = useState<TimeBasis | undefined>(DEFAULT_PRESET.controls?.time_basis);
  const [sources, setSources] = useState<Set<number>>(new Set());
  const [repMode, setRepMode] = useState<PriorRepMode | undefined>(DEFAULT_PRESET.controls?.prior_rep_mode);
  const [repHistory, setRepHistory] = useState<'current' | 'ever' | undefined>(DEFAULT_PRESET.controls?.prior_rep_history);
  const [priorReps, setPriorReps] = useState<Set<number>>(new Set());
  const [preset, setPreset] = useState<PresetId | undefined>(DEFAULT_PRESET.id);
  const [preview, setPreview] = useState<AssignmentPreview | null>(null);
  const [confirmReassign, setConfirmReassign] = useState(false);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [target, setTarget] = useState<string>('new');
  const [idemKey, setIdemKey] = useState(newIdempotencyKey);
  const [previewRequest, setPreviewRequest] = useState<AssignmentRequest | null>(null);
  const [showExcluded, setShowExcluded] = useState<ExclusionKind | null>(null);

  // Checking rows in the grid switches to them, choosing every match switches
  // to all; clearing either goes back to the first N leads.
  useEffect(() => {
    if (allMatching) setMode('all');
    else if (selectedIds.length > 0) setMode('manual');
    else setMode((m) => (m === 'manual' || m === 'all' ? 'first_n' : m));
  }, [selectedIds.length, allMatching]);

  const multi = recipients.length > 1;
  const byCounts = multi && splitBy === 'counts';
  const drawn = mode === 'first_n' || mode === 'random_n';
  const countSum = recipients.reduce((t, r) => t + r.count, 0);
  const setRecipient = (key: number, patch: Partial<Recipient>) =>
    setRecipients((cur) => cur.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  // Set counts: each rep's own count. Evenly with a draw: N each. Otherwise
  // the server shares the selected leads out evenly.
  const split: AssignmentShare[] = useMemo(() => recipients.map((r) => ({
    rep: Number(r.rep),
    ...(byCounts ? { count: r.count } : multi && drawn ? { count: n } : {}),
  })), [recipients, byCounts, multi, drawn, n]);

  const applyPreset = (id: PresetId) => {
    setPreset(id);
    const p = PRESETS.find((x) => x.id === id);
    if (!p?.controls) return; // custom keeps whatever is set
    setDup(p.controls.duplicate_mode);
    setQualifier(p.controls.history_qualifier);
    setBasis(p.controls.time_basis);
    setRepMode(p.controls.prior_rep_mode);
    setRepHistory(p.controls.prior_rep_history);
  };

  const controls: AssignmentControls = useMemo(() => ({
    duplicate_mode: dup,
    history_qualifier: dup && dup !== 'include_repeats' ? qualifier : undefined,
    time_basis: dup && dup !== 'include_repeats' ? basis : undefined,
    source_ids: dup === 'exclude_selected_sources' ? [...sources] : undefined,
    prior_rep_mode: repMode,
    prior_rep_history: repMode && repMode !== 'none' ? repHistory : undefined,
    prior_rep_ids: repMode === 'selected_reps' ? [...priorReps] : undefined,
  }), [dup, qualifier, basis, sources, repMode, repHistory, priorReps]);

  const selection: AssignmentSelection | null = useMemo(() => {
    if (!mode) return null;
    if (mode === 'manual') return { mode, lead_ids: selectedIds, view_filter: viewFilter };
    if (mode === 'all') return { mode, sort, desc, view_filter: viewFilter };
    return { mode, n: byCounts ? countSum : multi ? n * recipients.length : n, sort, desc, view_filter: viewFilter };
  }, [mode, selectedIds, n, sort, desc, viewFilter, byCounts, countSum, multi, recipients.length]);

  // Any change makes the preview stale: the operator must preview again.
  useEffect(() => { setPreview(null); setConfirmReassign(false); }, [split, selection, controls]);

  const missing: string[] = [];
  if (recipients.some((r) => !r.rep)) missing.push(multi ? 'a rep on every row' : 'a rep');
  if (byCounts && recipients.some((r) => !(r.count > 0))) missing.push('a count for every rep');
  if (!mode) missing.push('which leads');
  if (mode === 'manual' && selectedIds.length === 0) missing.push('selected rows');
  if (drawn && !byCounts && !(n > 0)) missing.push('how many');
  if (!preset) missing.push('a repeat rule');
  if (preset && !dup) missing.push('the duplicate filter');
  if (dup && dup !== 'include_repeats' && (!qualifier || !basis)) missing.push('what counts as an earlier duplicate');
  if (preset && !repMode) missing.push('the prior-rep filter');
  if (repMode && repMode !== 'none' && !repHistory) missing.push('current or ever assigned');
  if (repMode === 'selected_reps' && priorReps.size === 0) missing.push('which prior reps');
  if (dup === 'exclude_selected_sources' && sources.size === 0) missing.push('at least one source to skip');

  const doPreview = useMutation({
    mutationFn: async () => {
      const request: AssignmentRequest = { split, selection: selection as AssignmentSelection, controls };
      return { request, preview: await previewAssignment(request) };
    },
    onSuccess: ({ request, preview: p }) => { setPreview(p); setPreviewRequest(request); setIdemKey(newIdempotencyKey()); },
    onError: (e) => toast.error((e as Error).message),
  });

  const save = useMutation({
    mutationFn: async () => {
      if (!preview) throw new Error('Preview first');
      if (target === 'new') return saveAssignment(preview, idemKey, confirmReassign, reason || null, note || null);
      const a = (existing.data ?? []).find((x) => String(x.id) === target);
      if (!a) throw new Error('Assignment not found');
      return editAssignment(a.id, 'add', { selection: preview.selection, controls: preview.controls, items: preview.items, confirm_reassign: confirmReassign },
        a.version, idemKey, reason || null);
    },
    onSuccess: (r) => {
      const applied = (r as { applied?: number }).applied ?? 0;
      const conflicts = ((r as { conflict?: number }).conflict ?? 0) + ((r as { departed_from_draw?: number }).departed_from_draw ?? 0);
      const to = preview && preview.shares.length > 1 ? `${preview.shares.length} reps` : preview?.shares[0]?.name;
      if (conflicts > 0) toast.warning(`Assigned ${fmtInt(applied)}. ${fmtInt(conflicts)} leads changed since the preview and were left untouched.`);
      else toast.success(`Assigned ${fmtInt(applied)} leads to ${to}.`);
      setPreview(null); setReason(''); setNote(''); setConfirmReassign(false);
      queryClient.invalidateQueries({ queryKey: leadImportKeys.all });
      onSaved();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const activeReps = (reps.data ?? []).filter((r) => r.is_active);
  const inactiveReps = (reps.data ?? []).filter((r) => !r.is_active);
  // A single import lists the sources it shares phones with, with counts;
  // across imports every source is a choice.
  const sourceOptions: { id: number; name: string; shared?: number }[] = across
    ? (allSources.data ?? []).map((x) => ({ id: x.id, name: x.source }))
    : (summary?.overlap_sources ?? []).flatMap((o) => (o.lead_source == null ? [] : [{ id: o.lead_source, name: o.source ?? '', shared: o.phones }]));
  const sourceNames = new Map(sourceOptions.map((o) => [o.id, o.name]));
  // An earlier assignment can only grow with leads from the same scope, and only for one rep.
  const sameRepAssignments = multi ? [] : (existing.data ?? []).filter((a) => String(a.rep) === recipients[0].rep && a.batch === batchId);
  useEffect(() => { if (multi) setTarget('new'); }, [multi]);
  const needsReassign = (preview?.reassignments.length ?? 0) > 0;

  const sourceChecklist = (
          <div className="li-assign__sub">
            <span className="li-field__label">Exclude repeats of the checked sources</span>
            {sourceOptions.length === 0 ? <span className="li-muted">{across ? 'No lead sources yet.' : 'No lead in this import was received in another import.'}</span> : (
              <div className={`li-checklist${sourceOptions.length > 8 ? ' li-checklist--scroll' : ''}`}>
                {sourceOptions.map((o) => (
                  <Checkbox key={o.id} checked={sources.has(o.id)}
                    onChange={() => setSources((cur) => { const s = new Set(cur); if (s.has(o.id)) s.delete(o.id); else s.add(o.id); return s; })}
                    labelElement={<span>{o.name}{o.id === sourceId ? ' (this source)' : ''}{o.shared != null && <span className="li-muted"> {fmtInt(o.shared)} shared</span>}</span>} />
                ))}
              </div>
            )}
          </div>
  );
  const ruleSummary = describeRule(controls, sourceNames);
  const modeOptions: { value: Mode; label: string; hint: string }[] = [
    ...(selectedIds.length > 0 && !allMatching ? [{ value: 'manual' as const, label: `Checked (${fmtInt(selectedIds.length)})`, hint: 'The rows you checked in the list' }] : []),
    { value: 'all', label: matching != null ? `All matching (${fmtInt(matching)})` : 'All matching', hint: 'Every lead the filters match, not just the loaded rows' },
    { value: 'first_n', label: 'First N', hint: 'Top of the list, in the order shown' },
    { value: 'random_n', label: 'Random N', hint: across ? 'Drawn on the server from all the matching leads' : 'Drawn on the server from the whole filtered import' },
  ];

  return (
    <div className="li-assign" aria-label="Assign leads">
      <div className="li-assign__head">
        <h3>{multi ? 'Split leads between reps' : 'Give leads to a rep'}</h3>
        {summary && <span className="li-muted">{summary.eligible_unassigned === 0
          ? 'Every assignable lead here is already with a rep. Giving one out again moves it.'
          : `${fmtInt(summary.eligible_unassigned)} assignable ${summary.eligible_unassigned === 1 ? 'lead is' : 'leads are'} not with a rep yet`}</span>}
      </div>

      <section className="li-assign__sec">
        <div className="li-recipients__head">
          <span className="li-field__label">{multi ? 'Reps' : 'Rep'}</span>
          {multi && (
            <Segmented<'even' | 'counts'> ariaLabel="How to split" size="sm" value={splitBy} onChange={setSplitBy}
              options={[
                { value: 'even', label: 'Evenly', hint: 'Every rep gets the same number of leads' },
                { value: 'counts', label: 'Set counts', hint: 'Say how many leads each rep gets' },
              ]} />
          )}
        </div>
        <ul className="li-recipients">
          {recipients.map((r, i) => (
            <li key={r.key} className="li-recipients__row">
              <PickList value={r.rep} onChange={(v) => setRecipient(r.key, { rep: v })} fill ariaLabel={multi ? `Rep ${i + 1}` : 'Rep'} placeholder="Choose a rep…"
                options={activeReps.filter((a) => String(a.id) === r.rep || !recipients.some((o) => o.rep === String(a.id)))
                  .map((a) => ({ value: String(a.id), label: a.name }))} />
              {byCounts && (
                <NumericInput min={1} value={r.count} onValueChange={(v) => setRecipient(r.key, { count: Math.max(0, Math.floor(v || 0)) })}
                  buttonPosition="none" style={{ width: 76 }} aria-label={`Leads for rep ${i + 1}`} />
              )}
              {multi && (
                <Button minimal small icon="cross" aria-label={`Remove rep ${i + 1}`}
                  onClick={() => setRecipients((cur) => cur.filter((o) => o.key !== r.key))} />
              )}
            </li>
          ))}
        </ul>
        <div className="li-row li-wrap">
          <button type="button" className="li-btn li-btn--link" disabled={recipients.length >= activeReps.length}
            onClick={() => setRecipients((cur) => [...cur, newRecipient()])}>
            {multi ? 'Add a rep' : 'Split between several reps'}
          </button>
          {multi && byCounts && <span className="li-muted">{fmtInt(countSum)} leads in all</span>}
        </div>
        {multi && (
          <span className="li-muted">
            Leads are dealt out in turn, so every rep gets a fair share of the top of the list. Each rep gets their own saved assignment.
          </span>
        )}
      </section>

      <section className="li-assign__sec">
        <span className="li-field__label">Which leads</span>
        <Segmented<Mode>
          ariaLabel="Selection"
          value={mode}
          onChange={setMode}
          options={modeOptions}
        />
        {drawn && !byCounts && (
          <div className="li-row">
            <NumericInput min={1} value={n} onValueChange={(v) => setN(Math.max(0, Math.floor(v || 0)))} buttonPosition="none" style={{ width: 90 }} aria-label={multi ? 'How many each' : 'How many'} />
            <span className="li-muted">
              {!multi && matching != null && n >= matching
                ? `that is every one of the ${fmtInt(matching)} leads shown`
                : <>{multi ? `each, ${fmtInt(n * recipients.length)} in all, ` : ''}of the {matching != null && `${fmtInt(matching)} `}leads shown{mode === 'first_n' ? ', top first' : ''}</>}
            </span>
          </div>
        )}
        {drawn && byCounts && <span className="li-muted">The {mode === 'first_n' ? 'first' : 'random'} {fmtInt(countSum)} leads, as the counts above add up.</span>}
        {mode === 'all' && <span className="li-muted">{multi && !byCounts ? 'Every matching lead, shared out evenly.' : byCounts ? 'Up to the counts above; any matching leads beyond them stay unassigned.' : 'Every matching lead that passes the repeat rule.'}</span>}
        {mode === 'manual' && selectedIds.length === 0 && <span className="li-muted">Check rows in the list.</span>}
      </section>

      <section className="li-assign__sec">
        <label className="li-field">
          <span className="li-field__label">Repeats</span>
          <PickList value={preset ?? ''} onChange={(v) => applyPreset(v as PresetId)} fill ariaLabel="Repeat rule"
            options={PRESETS.map((p) => ({ value: p.id, label: p.title }))} />
        </label>
        <span className="li-muted">{preset === 'custom' ? `Rule: ${ruleSummary}` : PRESETS.find((p) => p.id === preset)?.desc}</span>
        {preset === 'skip_sources' && sourceChecklist}
      </section>

      {preset === 'custom' && (
        <>
        <section className="li-assign__sec">
          <span className="li-field__label">Repeat leads</span>
          <Segmented<DuplicateMode>
            ariaLabel="Duplicate filter"
            value={dup}
            onChange={setDup}
            options={[
              { value: 'include_repeats', label: 'Include repeats' },
              { value: 'exclude_any', label: 'Exclude repeats' },
              { value: 'exclude_selected_sources', label: 'By source' },
            ]}
          />
          {dup && dup !== 'include_repeats' && (
            <div className="li-assign__sub">
              <span className="li-field__label">Counts as a repeat when the other copy was</span>
              <Segmented<HistoryQualifier>
                ariaLabel="History qualifier"
                value={qualifier}
                onChange={setQualifier}
                options={[
                  { value: 'received', label: 'Received' },
                  { value: 'assigned_current', label: 'Assigned now' },
                  { value: 'assigned_ever', label: 'Ever assigned' },
                ]}
              />
              <span className="li-field__label">from</span>
              <Segmented<TimeBasis>
                ariaLabel="Time basis"
                value={basis}
                onChange={setBasis}
                options={[
                  { value: 'accepted_before', label: 'Imported before', hint: 'Imports accepted before the lead\'s own. Load order: for historical backloads this is not the real receipt order.' },
                  { value: 'earlier_batch_date', label: 'Earlier batch dates', hint: 'Batch dates before the lead\'s own import. Same date: the lower import number counts as earlier.' },
                  { value: 'all_known', label: 'Any other import', hint: 'Includes imports received after the lead\'s own.' },
                ]}
              />
            </div>
          )}
          {dup === 'exclude_selected_sources' && sourceChecklist}
        </section>

        <section className="li-assign__sec">
          <span className="li-field__label">Already given to a rep</span>
          <Segmented<PriorRepMode>
            ariaLabel="Prior rep filter"
            value={repMode}
            onChange={setRepMode}
            options={[
              { value: 'none', label: 'No filter' },
              { value: 'any_rep', label: 'Any rep' },
              { value: 'selected_reps', label: 'Selected reps' },
            ]}
          />
          {repMode && repMode !== 'none' && (
            <div className="li-assign__sub">
              <Segmented<'current' | 'ever'>
                ariaLabel="Prior rep history"
                value={repHistory}
                onChange={setRepHistory}
                options={[{ value: 'current', label: 'Assigned now' }, { value: 'ever', label: 'Ever assigned' }]}
              />
              <span className="li-muted">Checks other copies of the same phone in other imports, never the merchant&apos;s main rep.</span>
            </div>
          )}
          {repMode === 'selected_reps' && (
            <div className="li-checklist li-checklist--scroll">
              {activeReps.concat(inactiveReps).map((r) => (
                <Checkbox key={r.id} checked={priorReps.has(r.id)} label={r.name}
                  onChange={() => setPriorReps((cur) => { const s = new Set(cur); if (s.has(r.id)) s.delete(r.id); else s.add(r.id); return s; })} />
              ))}
            </div>
          )}
        </section>

        </>
      )}

      <div className="li-assign__go">
        <Button intent="primary" fill disabled={missing.length > 0} loading={doPreview.isPending} onClick={() => doPreview.mutate()}>Preview assignment</Button>
        {missing.length > 0 && <span className="li-muted">Choose {missing.join(', ')}.</span>}
        {doPreview.isPending && across && <span className="li-muted" aria-live="polite">Checking repeats across every import. This can take a few seconds.</span>}
      </div>

      {preview && (
        <section className="li-assign__preview" aria-live="polite">
          <div className="li-assign__big">
            <b>{fmtInt(preview.selected)}</b> leads to <b>{preview.shares.length > 1 ? `${preview.shares.length} reps` : preview.shares[0]?.name}</b>
            {preview.shortfall > 0 && <Pill tone="warn">{fmtInt(preview.shortfall)} short of {fmtInt(preview.requested)}</Pill>}
          </div>
          {preview.shares.length > 1 && (
            <table className="li-table li-table--compact li-shares">
              <thead><tr><th>Rep</th><th className="is-num">Leads</th><th className="is-num">Moved from another rep</th></tr></thead>
              <tbody>
                {preview.shares.map((sh) => (
                  <tr key={sh.rep}>
                    <td>{sh.name}</td>
                    <td className="is-num">
                      {fmtInt(sh.count)}
                      {sh.requested != null && sh.count < sh.requested && <span className="li-muted"> of {fmtInt(sh.requested)}</span>}
                    </td>
                    <td className="is-num">{sh.reassigned ? fmtInt(sh.reassigned) : <span className="li-muted">-</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {across && preview.imports > 0 && <span className="li-muted">From {fmtInt(preview.imports)} {preview.imports === 1 ? 'import' : 'imports'}.</span>}
          <dl className="li-dl li-dl--tight">
            <dt>Looked at</dt><dd>{fmtInt(preview.considered)}{preview.complete === false && <span className="li-muted" data-testid="li-preview-partial"> (stopped once there were enough; the rest were not checked)</span>}</dd>
            {preview.hard_excluded > 0 && (<><dt>Not assignable</dt><dd><button type="button" className="li-btn li-btn--link" title="See these leads" onClick={() => setShowExcluded('not_assignable')}>{fmtInt(preview.hard_excluded)}</button>{Object.keys(preview.hard_reasons).length > 0 && (
              <span className="li-muted"> ({Object.entries(preview.hard_reasons).map(([k, v]) => `${REASON_LABEL[k] ?? k} ${fmtInt(v)}`).join(', ')})</span>)}</dd></>)}
            {preview.duplicate_excluded > 0 && (<><dt>Skipped repeats</dt><dd><button type="button" className="li-btn li-btn--link" title="See these leads" onClick={() => setShowExcluded('repeat')}>{fmtInt(preview.duplicate_excluded)}</button>{preview.duplicate_excluded_by_source.length > 0 && (
              <span className="li-muted"> ({preview.duplicate_excluded_by_source.map((s) => `${s.source} ${fmtInt(s.count)}`).join(', ')})</span>)}</dd></>)}
            {preview.rep_excluded > 0 && (<><dt>A rep already had</dt><dd><button type="button" className="li-btn li-btn--link" title="See these leads" onClick={() => setShowExcluded('prior_rep')}>{fmtInt(preview.rep_excluded)}</button>{preview.rep_excluded_by_rep.length > 0 && (
              <span className="li-muted"> ({preview.rep_excluded_by_rep.map((r) => `${r.name} ${fmtInt(r.count)}`).join(', ')})</span>)}</dd></>)}
            {preview.unchanged > 0 && (<><dt>{preview.shares.length > 1 ? 'Already with their rep' : 'Already with this rep'}</dt><dd>{fmtInt(preview.unchanged)}</dd></>)}
          </dl>
          {preview.left_over > 0 && (
            <div className="li-note">{fmtInt(preview.left_over)} more matching {preview.left_over === 1 ? 'lead passes' : 'leads pass'} the repeat rule but {preview.left_over === 1 ? 'is' : 'are'} beyond the counts, and stay{preview.left_over === 1 ? 's' : ''} as {preview.left_over === 1 ? 'it is' : 'they are'}.</div>
          )}
          {preview.manual_not_selectable && preview.manual_not_selectable.length > 0 && (
            <div className="li-warn">{fmtInt(preview.manual_not_selectable.length)} of the checked rows are excluded and will not be assigned.</div>
          )}
          {needsReassign && (
            <div className="li-reassign">
              <div className="li-warn">
                <b>{fmtInt(preview.reassignments.length)}</b> selected leads already have a rep and will move{preview.shares.length > 1 ? '' : ` to ${preview.shares[0]?.name}`}:
                <ul>
                  {preview.reassignments.slice(0, 6).map((r) => (
                    <li key={r.lead}>{r.business_name || r.phone} (now {r.previous_rep_name}{preview.shares.length > 1 ? `, to ${r.rep_name}` : ''})</li>
                  ))}
                  {preview.reassignments.length > 6 && <li>and {fmtInt(preview.reassignments.length - 6)} more</li>}
                </ul>
              </div>
              <Checkbox checked={confirmReassign} onChange={(e) => setConfirmReassign((e.target as HTMLInputElement).checked)} label="Move these leads to the new rep" />
            </div>
          )}
          {(needsReassign || target !== 'new') && (
            <TextArea fill rows={2} value={reason} placeholder={needsReassign ? 'Reason for the reassignment (required)' : 'Reason (optional)'} onChange={(e) => setReason(e.target.value)} />
          )}
          {sameRepAssignments.length > 0 && (
            <label className="li-field">
              <span className="li-field__label">Save as</span>
              <PickList value={target} onChange={setTarget} fill ariaLabel="Save as"
                options={[{ value: 'new', label: 'New assignment' }, ...sameRepAssignments.map((a) => ({ value: String(a.id), label: `Add to the earlier one (${fmtInt(a.effective_count)} leads)` }))]} />
            </label>
          )}
          {target === 'new' && <InputGroup value={note} placeholder="Note (optional)" onChange={(e) => setNote(e.target.value)} />}
          <Button intent="success" fill disabled={preview.selected === 0 || (needsReassign && (!confirmReassign || !reason.trim()))}
            loading={save.isPending} onClick={() => save.mutate()}>
            Assign {fmtInt(preview.selected)} leads{preview.shares.length > 1 ? ` to ${preview.shares.length} reps` : ''}
          </Button>
          <span className="li-muted">Leads that change before you save are left out and reported.</span>
        </section>
      )}
      {showExcluded && previewRequest && <ExcludedLeadsDialog request={previewRequest} kind={showExcluded} onClose={() => setShowExcluded(null)} />}
      <ErrorNote error={existing.error} />
    </div>
  );
}
